import { useEffect, useRef, useState } from "react";
import Button from "../../../shared/components/Button";
import { TextField } from "../../../shared/components/form";
import type { NodeDefinition } from "../model";
import { Accordion } from "../../../shared/components/Accordion";
import "./NodePalette.scss";
import { kindColor } from "./nodeStyles";
import { NodeGlyph } from "./NodeVisuals";

const TWO_COLUMN_MIN_WIDTH = 320;

export function NodePalette({
  definitions,
  onAdd,
}: {
  definitions: NodeDefinition[];
  onAdd: (definition: NodeDefinition) => void;
}) {
  const [query, setQuery] = useState("");
  const [isWide, setIsWide] = useState(false);
  const paletteRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const palette = paletteRef.current;
    if (!palette) return;

    const updateWidth = () =>
      setIsWide(palette.clientWidth >= TWO_COLUMN_MIN_WIDTH);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(palette);
    return () => observer.disconnect();
  }, []);
  const groups = [
    { title: "Sources", category: "source" },
    { title: "Outputs", category: "output" },
    { title: "Frame processing", category: "frame" },
    { title: "Inference", category: "inference" },
    { title: "Logic and rules", category: "logic" },
    { title: "Analytics", category: "analytics" },
  ] as const;

  return (
    <div
      className={`node-palette${isWide ? " node-palette--wide" : ""}`}
      ref={paletteRef}
    >
      <TextField
        aria-label="Search workflow nodes"
        className="node-search"
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search nodes"
        type="search"
        value={query}
      />
      {groups.map((group, index) => {
        const matches = definitions.filter((definition) => {
          if (definition.hidden) return false;
          if (definition.category !== group.category) return false;
          const searchable = `${definition.label} ${definition.subtitle} ${definition.detail}`;
          return searchable.toLowerCase().includes(query.trim().toLowerCase());
        });

        if (!matches.length) return null;

        return (
          <Accordion
            key={`${group.category}-${query.trim() ? "search" : "browse"}`}
            count={matches.length}
            defaultOpen={index === 0 || query.trim().length > 0}
            icon={<NodeGlyph category={group.category} />}
            title={group.title}
          >
            <div className="items">
              {matches.map((definition) => {
                const iconName = kindColor(definition.category);

                return (
                  <Button
                    className={`item item--${iconName}`}
                    draggable
                    icon={
                      <NodeGlyph
                        category={definition.category}
                        className="button-icon"
                      />
                    }
                    key={definition.kind}
                    onClick={() => onAdd(definition)}
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "copy";
                      event.dataTransfer.setData(
                        "application/x-owl-workflow-node",
                        definition.kind,
                      );
                    }}
                    variant="secondary"
                  >
                    {definition.label}
                  </Button>
                );
              })}
            </div>
          </Accordion>
        );
      })}
    </div>
  );
}
